import React, { useState, useEffect } from 'react';
import { ArrowLeft, Users, UserMinus, Plus, UserPlus } from 'lucide-react';
import { contactService } from '../../services/contactService';
import toast from 'react-hot-toast';

const GroupDetailsView = ({ 
  group, 
  onBack, 
  onContactSelect, 
  onRemoveContact, 
  onAddContacts,
  onRefresh 
}) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (group) {
      loadGroupContacts();
    }
  }, [group]);

  const loadGroupContacts = async () => {
    if (!group?.groupId) return;
    
    setLoading(true);
    try {
      const groupDetails = await contactService.getContactGroupDetails(group.groupId);
      setContacts(groupDetails.members || []);
    } catch (error) {
      console.error('Error loading group contacts:', error);
      toast.error('Failed to load group contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleContactClick = (contactId) => {
    if (onContactSelect) {
      onContactSelect(contactId);
    }
  };

  const handleRemoveContact = async (contact) => {
    if (onRemoveContact) {
      await onRemoveContact(contact);
      // Refresh the contact list after removal
      await loadGroupContacts();
    }
  };

  const handleAddContacts = () => {
    if (onAddContacts) {
      onAddContacts();
    }
  };

  // Refresh handler - expose loadGroupContacts to parent via ref
  useEffect(() => {
    if (onRefresh) {
      onRefresh.current = loadGroupContacts;
    }
  }, [onRefresh, group]);

  if (!group) {
    return null;
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors md:hidden"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">{group.groupName}</h2>
            {group.description && (
              <p className="text-sm text-gray-500 mt-1">{group.description}</p>
            )}
          </div>
          <button
            onClick={handleAddContacts}
            className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors flex items-center gap-2"
            title="Add contacts to group"
          >
            <UserPlus className="w-5 h-5" />
            <span className="hidden sm:inline text-sm font-medium">Add Contacts</span>
          </button>
        </div>

        {/* Statistics */}
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <Users className="w-4 h-4 text-primary-600" />
            <span>{group.totalContacts || contacts.length} contacts</span>
          </div>
          {group.subscribedCount !== undefined && (
            <div className="flex items-center gap-1">
              <span className="text-green-600">{group.subscribedCount} subscribed</span>
            </div>
          )}
          {group.unsubscribedCount !== undefined && group.unsubscribedCount > 0 && (
            <div className="flex items-center gap-1">
              <span className="text-red-600">{group.unsubscribedCount} unsubscribed</span>
            </div>
          )}
        </div>
      </div>

      {/* Contacts List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : contacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Users className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg mb-2">No contacts in this group</p>
            <button
              onClick={handleAddContacts}
              className="px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Contacts
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {contacts.map((contact) => (
              <div
                key={contact.contactId}
                onClick={() => handleContactClick(contact.contactId)}
                className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {contact.contactName || 'Unnamed'}
                  </p>
                  <p className="text-sm text-gray-500 truncate">{contact.phoneNumber}</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveContact(contact);
                  }}
                  className="p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors ml-2"
                  title="Remove from group"
                >
                  <UserMinus className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupDetailsView;

